// The start: the files of each side in order (a book's chapters), the two languages, and Align;
// or a saved session or a TMX to reopen; or the autosaved session to resume. In Chrome and Edge
// the files can come from a working folder that saves and exports go back to (decision 14).

import { useEffect, useRef, useState } from 'react'
import { byName, DOCUMENT, folderAllowed, folderFiles, folderSupported, forgetFolder, pickFolder, readFromFolder, rememberedFolder, SESSION_SUFFIX } from '../core/files.ts'
import { getPref, setPref } from '../core/prefs.ts'
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../core/languages.ts'
import type { Column } from '../core/types.ts'
import type { Autosaved } from '../engine/protocol.ts'
import { Wordmark } from '../../app/Wordmark.tsx'
import { desktop } from './desktop.ts'

export interface OpenRequest {
  kind: 'align'
  source: File[]
  target: File[]
  sourceLanguage: string
  targetLanguage: string
}

interface Props {
  autosaved: Autosaved | null
  error: string | null
  folder: FileSystemDirectoryHandle | null
  onFolder: (folder: FileSystemDirectoryHandle | null) => void
  onAlign: (request: OpenRequest) => void
  onReopen: (file: File) => void
  onResume: () => void
  onDiscard: () => void
}

export function OpenScreen(p: Props) {
  const [files, setFiles] = useState<Record<Column, File[]>>({ Source: [], Target: [] })
  const [sourceLanguage, setSourceLanguage] = useState(() => getPref('sourceLanguage', 'en'))
  const [targetLanguage, setTargetLanguage] = useState(() => getPref('targetLanguage', 'th'))
  // The banner is about browsers; the desktop app saves through its own panel (brief 46).
  const [banner, setBanner] = useState(() => !desktop && !folderSupported && !getPref('bannerDismissed', false))
  const [listing, setListing] = useState<string[] | null>(null)
  const [waiting, setWaiting] = useState<FileSystemDirectoryHandle | null>(null)
  const reopen = useRef<HTMLInputElement>(null)

  // The folder chosen on an earlier visit: listed if the browser still allows it, otherwise
  // one click asks again.
  useEffect(() => {
    if (p.folder) {
      void folderFiles(p.folder).then(setListing, () => setListing(null))
      return
    }
    setListing(null)
    void rememberedFolder().then(async folder => {
      if (!folder) return
      if (await folderAllowed(folder, false)) p.onFolder(folder)
      else setWaiting(folder)
    })
  }, [p.folder])

  const add = (column: Column, more: File[]) => {
    const documents = more.filter(f => DOCUMENT.test(f.name)).sort((a, b) => byName(a.name, b.name))
    setFiles(f => ({ ...f, [column]: [...f[column], ...documents] }))
  }
  const addFromFolder = async (column: Column, name: string) => {
    if (p.folder) add(column, [await readFromFolder(p.folder, name)])
  }
  const reorder = (column: Column, k: number, step: number) =>
    setFiles(f => {
      const list = [...f[column]]
      const [moved] = list.splice(k, 1)
      if (moved) list.splice(Math.max(0, Math.min(list.length, k + step)), 0, moved)
      return { ...f, [column]: list }
    })
  const remove = (column: Column, k: number) => setFiles(f => ({ ...f, [column]: f[column].filter((_, j) => j !== k) }))

  const align = () => {
    setPref('sourceLanguage', sourceLanguage)
    setPref('targetLanguage', targetLanguage)
    p.onAlign({ kind: 'align', source: files.Source, target: files.Target, sourceLanguage, targetLanguage })
  }

  const chooseFolder = async () => {
    try {
      p.onFolder(await pickFolder())
      setWaiting(null)
    } catch {
      // The person closed the picker.
    }
  }

  const ready = files.Source.length > 0 && files.Target.length > 0

  return (
    <div className="screen">
      <div className="sheet">
        <div className="brand">
          <Wordmark />
          <span className="app-name">Align</span>
        </div>
        <h1>Align a document with its translation</h1>
        <p className="lead">Pair each sentence with its translation, correct the pairs, and {desktop ? 'save' : 'download'} them as TMX for Interplae’s translation memory. Your files stay on this computer: nothing is uploaded.</p>

        {banner && (
          <div className="banner" role="note" data-banner="folder">
            <p>In Chrome or Edge this page can work in a folder on disk, reading your documents from it and saving there directly. In this browser, open files and download what you save.</p>
            <button className="link" onClick={() => (setBanner(false), setPref('bannerDismissed', true))}>Dismiss</button>
          </div>
        )}

        {p.autosaved && (
          <div className="reopen" data-autosaved>
            <span className="what">
              <b>{p.autosaved.name}</b>, {p.autosaved.rows.toLocaleString()} rows, autosaved {new Date(p.autosaved.savedAt).toLocaleString()}.
            </span>
            <button className="primary" onClick={p.onResume} data-resume>Resume</button>
            <button onClick={p.onDiscard}>Discard</button>
          </div>
        )}

        {p.error && <p className="status-line alert" role="alert">{p.error}</p>}

        {folderSupported && (
          <div className="folder">
            {p.folder ? (
              <>
                <div className="folder-head">
                  <span>
                    Working in the folder <b>{p.folder.name}</b>. Saves and exports go here.
                  </span>
                  <button onClick={() => void chooseFolder()}>Another folder…</button>
                  <button className="link" onClick={() => (void forgetFolder(), p.onFolder(null))}>Stop using it</button>
                </div>
                {listing && (
                  <ul className="folder-files">
                    {listing.filter(n => DOCUMENT.test(n) || n.endsWith(SESSION_SUFFIX) || /\.tmx$/i.test(n)).map(name => (
                      <li key={name}>
                        <span className="fname">{name}</span>
                        {DOCUMENT.test(name) ? (
                          <>
                            <button onClick={() => void addFromFolder('Source', name)}>Source</button>
                            <button onClick={() => void addFromFolder('Target', name)}>Target</button>
                          </>
                        ) : (
                          <button onClick={() => void readFromFolder(p.folder!, name).then(p.onReopen)}>Open</button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : waiting ? (
              <div className="folder-head">
                <span>Last time you worked in the folder <b>{waiting.name}</b>.</span>
                <button onClick={() => void folderAllowed(waiting, true).then(ok => ok && (p.onFolder(waiting), setWaiting(null)))}>Use it again</button>
                <button onClick={() => void chooseFolder()}>Another folder…</button>
              </div>
            ) : (
              <div className="folder-head">
                <span>Work in a folder on disk: open its documents and save back into it.</span>
                <button onClick={() => void chooseFolder()} data-folder>Choose a folder…</button>
              </div>
            )}
          </div>
        )}

        <div className="sides">
          <Side
            title="Source"
            column="Source"
            language={sourceLanguage}
            languages={SOURCE_LANGUAGES}
            onLanguage={setSourceLanguage}
            files={files.Source}
            onAdd={f => add('Source', f)}
            onMove={(k, s) => reorder('Source', k, s)}
            onRemove={k => remove('Source', k)}
          />
          <Side
            title="Translation"
            column="Target"
            language={targetLanguage}
            languages={TARGET_LANGUAGES.filter(([t]) => t !== sourceLanguage)}
            onLanguage={setTargetLanguage}
            files={files.Target}
            onAdd={f => add('Target', f)}
            onMove={(k, s) => reorder('Target', k, s)}
            onRemove={k => remove('Target', k)}
          />
        </div>

        <div className="actions">
          <button className="primary" disabled={!ready || sourceLanguage === targetLanguage} onClick={align} data-align>Align</button>
          <span className="muted">or</span>
          <button onClick={() => reopen.current?.click()} data-reopen>Open a saved session or a TMX…</button>
          <input
            ref={reopen}
            type="file"
            accept={`${SESSION_SUFFIX},.json,.tmx`}
            hidden
            data-input="reopen"
            onChange={e => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file) p.onReopen(file)
            }}
          />
        </div>
        <p className="foot muted">DOCX, Markdown and plain text, several files on a side in reading order. Footnotes and endnotes are aligned after the body; headers and footers are left out.</p>
      </div>
    </div>
  )
}

interface SideProps {
  title: string
  column: Column
  language: string
  languages: readonly (readonly [string, string])[]
  onLanguage: (tag: string) => void
  files: File[]
  onAdd: (files: File[]) => void
  onMove: (k: number, step: number) => void
  onRemove: (k: number) => void
}

function Side(p: SideProps) {
  const input = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  return (
    <section
      className={`side${over ? ' over' : ''}`}
      data-side={p.column}
      onDragOver={e => (e.preventDefault(), setOver(true))}
      onDragLeave={() => setOver(false)}
      onDrop={e => {
        e.preventDefault()
        setOver(false)
        p.onAdd([...e.dataTransfer.files])
      }}
    >
      <div className="side-head">
        <h2>{p.title}</h2>
        <select value={p.language} onChange={e => p.onLanguage(e.target.value)} aria-label={`${p.title} language`} data-language={p.column}>
          {p.languages.map(([tag, name]) => (
            <option key={tag} value={tag}>{name}</option>
          ))}
        </select>
      </div>
      {p.files.length ? (
        <ol className="files">
          {p.files.map((f, k) => (
            <li key={`${f.name}-${k}`}>
              <span className="fname">{f.name}</span>
              <button className="tool" title="Earlier" disabled={k === 0} onClick={() => p.onMove(k, -1)}>↑</button>
              <button className="tool" title="Later" disabled={k === p.files.length - 1} onClick={() => p.onMove(k, 1)}>↓</button>
              <button className="tool" title="Remove" onClick={() => p.onRemove(k)}>✕</button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="drop muted">Drop files here, or add them.</p>
      )}
      <button onClick={() => input.current?.click()} data-add={p.column}>Add files…</button>
      <input
        ref={input}
        type="file"
        multiple
        accept=".docx,.md,.markdown,.txt"
        hidden
        data-input={p.column}
        onChange={e => {
          p.onAdd([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />
    </section>
  )
}
